import { create } from 'zustand';

/**
 * Manager approval ("override") flow. When the API answers 403 with details.overridable,
 * withOverride() opens the approval dialog (<OverrideHost/>), gets a single-use token from a
 * manager's PIN, and retries the same request with X-Override-Token.
 */
export const useOverrideStore = create((set) => ({
  request: null, // { permission, message, resolve }
  open: (permission, message) =>
    new Promise((resolve) => {
      set({ request: { permission, message, resolve } });
    }),
  close: (token = null) =>
    set((s) => {
      s.request?.resolve(token);
      return { request: null };
    }),
}));

export class OverrideCancelled extends Error {
  constructor() {
    super('Approval cancelled');
    this.name = 'OverrideCancelled';
    this.cancelled = true;
  }
}

/** fn(headers) → promise. Retries once with the manager's token if approval is required. */
export async function withOverride(fn) {
  try {
    return await fn({});
  } catch (err) {
    if (err?.status !== 403 || !err.details?.overridable) throw err;
    const token = await useOverrideStore.getState().open(err.details.permission, err.message);
    if (!token) throw new OverrideCancelled();
    return fn({ 'X-Override-Token': token });
  }
}
